// ─── WorkRoamer AI Architecture Standard — Cost Tracker ──────────────────────
//
// Every AI call (Layer 2 background + Layer 3 on-demand) is logged here.
// Provides administrators with:
//   • Total request counts by layer and operation
//   • Token usage tracking
//   • Estimated USD cost (using GPT-4o pricing as the baseline)
//   • Cache hit rate
//   • Daily/monthly aggregation
//   • Duplicate request prevention telemetry

import type { AIRequestLog, AIUsageStats, AIProviderName } from './types';

// ─── Config ───────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad:ai-cost-log:v1';
const MAX_LOG_ENTRIES = 1_000;  // Rolling window — oldest are evicted

// Token cost estimates (USD per 1,000 tokens) using GPT-4o pricing.
// Update these when switching providers.
const COST_PER_1K: Record<AIProviderName, { input: number; output: number }> = {
  'workroamer-default': { input: 0.0025, output: 0.010 },
  'openai':             { input: 0.0025, output: 0.010 },
  'anthropic':          { input: 0.003,  output: 0.015 },
  'google':             { input: 0.00125, output: 0.005 },
  'custom':             { input: 0.0,    output: 0.0   },
};

// ─── Storage ─────────────────────────────────────────────────────────────────

let _log: AIRequestLog[] | null = null;

function load(): AIRequestLog[] {
  if (_log !== null) return _log;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { _log = JSON.parse(raw) as AIRequestLog[]; return _log; }
  } catch { /* ignore */ }
  _log = [];
  return _log;
}

function persist(): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_log ?? [])); } catch { /* ignore */ }
}

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const AICostTracker = {
  /**
   * Estimate token count from a text string.
   * Rule of thumb: ~4 characters per token for English prose.
   */
  estimateTokens(text: string): number {
    return Math.ceil(text.length / 4);
  },

  /**
   * Estimate cost in USD for a completed AI call.
   */
  estimateCostUsd(
    inputTokens: number,
    outputTokens: number,
    provider: AIProviderName = 'workroamer-default',
  ): number {
    const rates = COST_PER_1K[provider] ?? COST_PER_1K['workroamer-default'];
    return (inputTokens / 1_000) * rates.input + (outputTokens / 1_000) * rates.output;
  },

  /**
   * Log a completed AI request (real call or cache hit).
   * Call this AFTER the API call returns (or the cache is checked).
   */
  logRequest(
    partial: Omit<AIRequestLog, 'id' | 'timestamp' | 'estimatedCostUsd'> & { estimatedCostUsd?: number },
  ): AIRequestLog {
    const log = load();
    const costUsd = partial.estimatedCostUsd
      ?? (partial.cacheHit
        ? 0
        : AICostTracker.estimateCostUsd(
            partial.estimatedInputTokens,
            partial.estimatedOutputTokens,
            partial.provider,
          ));

    const entry: AIRequestLog = {
      ...partial,
      id: uid(),
      timestamp: new Date().toISOString(),
      estimatedCostUsd: costUsd,
    };
    log.push(entry);
    // Rolling eviction — keep newest MAX_LOG_ENTRIES
    if (log.length > MAX_LOG_ENTRIES) {
      _log = log.slice(log.length - MAX_LOG_ENTRIES);
    }
    persist();
    return entry;
  },

  /**
   * Shorthand: log a cache hit (zero cost, instant).
   */
  logCacheHit(
    operation: string,
    itemId: string | null,
    layer: 1 | 2 | 3,
    provider: AIProviderName = 'workroamer-default',
  ): void {
    AICostTracker.logRequest({
      layer,
      operation,
      itemId,
      estimatedInputTokens: 0,
      estimatedOutputTokens: 0,
      estimatedCostUsd: 0,
      status: 'cache_hit',
      cacheHit: true,
      durationMs: 0,
      provider,
    });
  },

  /** Get aggregated usage statistics for the admin dashboard. */
  getStats(): AIUsageStats {
    const log = load();
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

    const thisMonth = log.filter(e => e.timestamp >= monthStart);
    const today = log.filter(e => e.timestamp >= todayStart);

    const hits   = log.filter(e => e.cacheHit).length;
    const misses = log.filter(e => !e.cacheHit).length;

    const byLayer: Record<string, number> = {};
    const byOp: Record<string, number> = {};
    for (const e of log) {
      byLayer[e.layer] = (byLayer[e.layer] ?? 0) + 1;
      byOp[e.operation] = (byOp[e.operation] ?? 0) + 1;
    }

    const totalInput  = log.filter(e => !e.cacheHit).reduce((s, e) => s + e.estimatedInputTokens, 0);
    const totalOutput = log.filter(e => !e.cacheHit).reduce((s, e) => s + e.estimatedOutputTokens, 0);
    const totalCost   = log.filter(e => !e.cacheHit).reduce((s, e) => s + e.estimatedCostUsd, 0);
    const monthCost   = thisMonth.filter(e => !e.cacheHit).reduce((s, e) => s + e.estimatedCostUsd, 0);
    const todayCost   = today.filter(e => !e.cacheHit).reduce((s, e) => s + e.estimatedCostUsd, 0);

    const realCalls = log.filter(e => !e.cacheHit && e.durationMs > 0);
    const avgMs = realCalls.length
      ? Math.round(realCalls.reduce((s, e) => s + e.durationMs, 0) / realCalls.length)
      : 0;

    return {
      totalRequests:         log.length,
      cacheHits:             hits,
      cacheMisses:           misses,
      cacheHitRate:          hits / Math.max(1, hits + misses),
      estimatedTotalCostUsd: totalCost,
      estimatedMonthlyCostUsd: monthCost,
      estimatedTodayCostUsd: todayCost,
      totalInputTokens:      totalInput,
      totalOutputTokens:     totalOutput,
      requestsByLayer:       byLayer,
      requestsByOperation:   byOp,
      recentRequests:        log.slice(-20).reverse(),
      layer2Count:           log.filter(e => e.layer === 2).length,
      layer3Count:           log.filter(e => e.layer === 3).length,
      failedCount:           log.filter(e => e.status === 'failed').length,
      averageProcessingMs:   avgMs,
    };
  },

  /** Return the full request log, newest first. Pass limit to cap results. */
  getLog(limit = 100): AIRequestLog[] {
    return load().slice(-limit).reverse();
  },

  clearLog(): void {
    _log = [];
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  },

  /** Return the rolling log size for diagnostic display */
  getLogSize(): number {
    return load().length;
  },
};
